import { forwardRef, InputHTMLAttributes, ReactNode, useEffect, useId, useState } from 'react';
import { cn } from '../../../utils/cn';
import { toPaise } from '../../../utils/currency';
import { controlClass, describedBy, Field, FieldOwnProps } from '../Field/Field';
import { Icon } from '../Icon/Icon';
import { IconButton } from '../IconButton/IconButton';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix'>, FieldOwnProps {
  leftIcon?: string;
  prefix?: ReactNode;
  rightElement?: ReactNode;
  size?: 'sm' | 'md';
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, required, leftIcon, prefix, rightElement, size = 'md', className, containerClassName, id, labelAction, ...rest },
  ref,
) {
  const autoId = useId();
  const inputId = id || autoId;
  const hasLeft = leftIcon || prefix;
  return (
    <Field label={label} hint={hint} error={error} required={required} htmlFor={inputId} className={containerClassName} labelAction={labelAction}>
      <div className="relative">
        {hasLeft && (
          <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-400">
            {leftIcon ? <Icon name={leftIcon} size={18} /> : <span className="text-sm font-medium text-ink-500">{prefix}</span>}
          </span>
        )}
        <input
          ref={ref}
          id={inputId}
          required={required}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy(inputId, error, hint)}
          className={cn(
            controlClass({ error, disabled: rest.disabled }),
            size === 'sm' ? 'h-9 text-sm' : 'h-10 text-sm',
            hasLeft ? 'pl-10' : 'pl-3.5',
            rightElement ? 'pr-12' : 'pr-3.5',
            className,
          )}
          {...rest}
        />
        {rightElement && <span className="absolute right-1 top-1/2 -translate-y-1/2">{rightElement}</span>}
      </div>
    </Field>
  );
});

export const PasswordInput = forwardRef<HTMLInputElement, InputProps>(function PasswordInput(props, ref) {
  const [show, setShow] = useState(false);
  return (
    <Input
      ref={ref}
      type={show ? 'text' : 'password'}
      autoComplete="current-password"
      rightElement={
        <IconButton size="sm" icon={show ? 'eyeOff' : 'eye'} label={show ? 'Hide password' : 'Show password'} onClick={() => setShow((s) => !s)} />
      }
      {...props}
    />
  );
});

/**
 * Money field. Shows rupees to the user, but `value` / `onChange` use paise
 * (what the backend expects). 1250.50 typed -> onChange(125050).
 */
export interface MoneyInputProps extends Omit<InputProps, 'value' | 'onChange'> {
  /** Paise */
  value?: number | null;
  onChange?: (paise: number | null) => void;
}

export function MoneyInput({ value, onChange, ...rest }: MoneyInputProps) {
  const [text, setText] = useState(value == null ? '' : String(value / 100));

  useEffect(() => {
    if (value !== toPaise(text)) setText(value == null ? '' : String(value / 100));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <Input
      {...rest}
      prefix="₹"
      inputMode="decimal"
      autoComplete="off"
      value={text}
      onChange={(e) => {
        const t = e.target.value.replace(/[^\d.]/g, '');
        if ((t.match(/\./g) || []).length > 1 || /\.\d{3,}$/.test(t)) return;
        setText(t);
        onChange?.(toPaise(t));
      }}
    />
  );
}

export default Input;
